import { calgaryDateString } from '../../../../../../utils/calgaryTime';
import jsPDF from 'jspdf';
import { computeBilledTimeAmount } from '../../../../../../helpers/billingHelpers';

const generateInvoicePDF = (
  matterId,
  invoiceFrom,
  client,
  dueDate,
  matterName,
  lineItems,
  discountRate,
  taxRate,
  invoiceId,
  application_no,
  country,
  url,
  status,
  billingPeriod,
  terms,
  gstNumber,
  issueDate,
  dataDump,
  blackAndWhite = false,
  amountPaid = 0,
  poNumber = '',
  lsdNumber = '',
  notes = ''
) => {
  const colors = blackAndWhite
    ? {
        primary: [40, 40, 40],
        primaryDark: [20, 20, 20],
        text: [0, 0, 0],
        lightText: [100, 100, 100],
        accent: [230, 230, 230],
        white: [255, 255, 255],
        border: [160, 160, 160],
        rowAlt: [242, 242, 242],
      }
    : {
        primary: [41, 128, 185],
        primaryDark: [31, 97, 141],
        text: [44, 62, 80],
        lightText: [127, 140, 141],
        accent: [236, 240, 241],
        white: [255, 255, 255],
        border: [189, 195, 199],
        rowAlt: [245, 248, 250],
      };

  const safe = (val) => (val != null ? String(val) : '');

  const fmtMoney = (val) => {
    const num = parseFloat(val) || 0;
    const fixed = num.toFixed(2);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? date : new Date(date).toISOString().slice(0, 10);
    const parts = d.split('-');
    if (parts.length === 3) {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ];
      return `${months[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}, ${parts[0]}`;
    }
    return d;
  };

  if (!Array.isArray(lineItems)) {
    console.error('Expected lineItems to be an array. Got:', lineItems);
    lineItems = [];
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const leftMargin = 15;
  const rightEdge = 195;
  const contentWidth = rightEdge - leftMargin;

  const colName = leftMargin;
  const colCode = 52;
  const colHrEnd = 100;
  const colRateEnd = 132;
  const summaryBarX = 120;

  const headerHeight = 7;
  const rowHeight = 8;

  doc.setFillColor(...colors.white);
  doc.rect(0, 0, 210, 297, 'F');

  let y = 18;
  const detLabelX = 140;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...colors.text);
  const companyNameMaxW = detLabelX - leftMargin - 4;
  const companyNameLines = safe(invoiceFrom?.from_name)
    .split('\n')
    .flatMap((seg) => doc.splitTextToSize(seg, companyNameMaxW));
  doc.text(companyNameLines, leftMargin, y);
  if (companyNameLines.length > 1) y += (companyNameLines.length - 1) * 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...colors.primary);
  doc.text('Invoice', rightEdge, 18, { align: 'right' });

  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...colors.text);

  const fromMaxW = detLabelX - leftMargin - 4;

  if (invoiceFrom?.address) {
    const addrLines = safe(invoiceFrom.address).split('\n').filter(Boolean);
    addrLines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line, fromMaxW);
      wrapped.forEach((wl) => { doc.text(wl, leftMargin, y); y += 4; });
    });
    if (!addrLines.length) y += 4;
  } else {
    y += 4;
  }
  if (invoiceFrom?.phone) {
    const wrapped = doc.splitTextToSize(`P - ${safe(invoiceFrom.phone)}`, fromMaxW);
    wrapped.forEach((wl) => { doc.text(wl, leftMargin, y); y += 4; });
  }
  if (invoiceFrom?.email) {
    const wrapped = doc.splitTextToSize(safe(invoiceFrom.email), fromMaxW);
    wrapped.forEach((wl) => { doc.text(wl, leftMargin, y); y += 4; });
  }

  let detY = 26;
  const detValueX = rightEdge;

  const detailRows = [
    { label: 'Invoice Number:', value: safe(invoiceId) },
    { label: 'Date:', value: formatDate(issueDate || calgaryDateString()) },
    { label: 'Project #:', value: safe(matterId) },
  ];
  if (application_no) {
    detailRows.push({ label: 'AFE #:', value: safe(application_no) });
  }
  if (lsdNumber) {
    detailRows.push({ label: 'LSD #:', value: safe(lsdNumber) });
  }
  if (poNumber) {
    detailRows.push({ label: 'PO #:', value: safe(poNumber) });
  }

  const detLineHeight = 4;
  detailRows.forEach((d) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...colors.primary);
    doc.text(d.label, detLabelX, detY);
    const labelW = typeof doc.getTextWidth === 'function' ? doc.getTextWidth(d.label) : 0;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.text);
    // Wrap the value into the space to the right of the label so a long value
    // (e.g. a long Project #) breaks onto extra right-aligned lines instead of
    // running left underneath the label.
    const valueMaxW = Math.max(20, detValueX - detLabelX - labelW - 2);
    const valueLines = doc.splitTextToSize(String(d.value ?? ''), valueMaxW);
    valueLines.forEach((line, i) => {
      doc.text(line, detValueX, detY + i * detLineHeight, { align: 'right' });
    });
    detY += Math.max(5, valueLines.length * detLineHeight + 1);
  });

  y = Math.max(y, detY) + 4;

  const billToBarY = y;
  const billToBarW = 80;
  doc.setFillColor(...colors.primary);
  doc.rect(leftMargin, billToBarY, billToBarW, 6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...colors.white);
  doc.text('BILL TO', leftMargin + 2, billToBarY + 4.5);

  const amtBoxX = 130;
  const amtBoxW = rightEdge - amtBoxX;
  doc.setFillColor(...colors.primary);
  doc.rect(amtBoxX, billToBarY, amtBoxW, 6, 'F');
  doc.setTextColor(...colors.white);
  doc.text('AMOUNT DUE', amtBoxX + 2, billToBarY + 4.5);

  let previewTotal = 0;
  lineItems.forEach((item) => {
    const qty = parseFloat(item.qty) || 0;
    const rate = parseFloat(item.amount) || 0;
    const lineAmt = qty * rate;
    const lineDisc = lineAmt * ((parseFloat(item.discount) || 0) / 100);
    previewTotal += lineAmt - lineDisc;
  });
  if (discountRate > 0) previewTotal -= previewTotal * (discountRate / 100);
  const previewTax = previewTotal * ((taxRate || 0) / 100);
  const previewFinal = previewTotal + previewTax;
  const previewAmountDue = previewFinal - (parseFloat(amountPaid) || 0);

  const amtValY = billToBarY + 6;
  doc.setFillColor(...colors.accent);
  doc.rect(amtBoxX, amtValY, amtBoxW, 10, 'F');
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.2);
  doc.rect(amtBoxX, amtValY, amtBoxW, 10, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...colors.text);
  doc.text(`$ ${fmtMoney(previewAmountDue)}`, amtBoxX + amtBoxW / 2, amtValY + 7.5, { align: 'center' });

  const termsBoxY = amtValY + 10;
  doc.setFillColor(...colors.accent);
  doc.rect(amtBoxX, termsBoxY, amtBoxW, 10, 'F');
  doc.setDrawColor(...colors.border);
  doc.rect(amtBoxX, termsBoxY, amtBoxW, 10, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...colors.primary);
  doc.text('TERMS', amtBoxX + amtBoxW / 2, termsBoxY + 4, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...colors.text);
  doc.text(safe(terms) || 'Net 30', amtBoxX + amtBoxW / 2, termsBoxY + 8.5, { align: 'center' });

  let bty = billToBarY + 10;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...colors.text);

  // Keep Bill To text within the BILL TO box width (leaves a 2mm inset on each side)
  const billToMaxW = billToBarW - 4;

  if (client?.address) {
    const clientAddrLines = safe(client.address).split('\n').filter(Boolean);
    clientAddrLines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line, billToMaxW);
      wrapped.forEach((wl) => { doc.text(wl, leftMargin + 2, bty); bty += 4; });
    });
    if (!clientAddrLines.length) bty += 4;
  }
  if (client?.name) {
    const wrapped = doc.splitTextToSize(safe(client.name), billToMaxW);
    wrapped.forEach((wl) => { doc.text(wl, leftMargin + 2, bty); bty += 4; });
  }
  if (client?.id) {
    const wrapped = doc.splitTextToSize(`Client ID: ${safe(client.id)}`, billToMaxW);
    wrapped.forEach((wl) => { doc.text(wl, leftMargin + 2, bty); bty += 4; });
  }
  if (client?.company) {
    const wrapped = doc.splitTextToSize(safe(client.company), billToMaxW);
    wrapped.forEach((wl) => { doc.text(wl, leftMargin + 2, bty); bty += 4; });
  }
  if (country) {
    const wrapped = doc.splitTextToSize(safe(country), billToMaxW);
    wrapped.forEach((wl) => { doc.text(wl, leftMargin + 2, bty); bty += 4; });
  }

  y = Math.max(bty, termsBoxY + 10) + 6;

  if (billingPeriod) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...colors.text);
    doc.text(`BILLING PERIOD: ${safe(billingPeriod)}`, leftMargin, y);
    y += 8;
  }

  const tableTop = y;

  doc.setFillColor(...colors.primary);
  doc.rect(leftMargin, tableTop, contentWidth, headerHeight, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...colors.white);
  doc.text('NAME', colName + 2, tableTop + 5);
  doc.text('COST CODE', colCode + 2, tableTop + 5);
  doc.text('HOURS', colHrEnd - 2, tableTop + 5, { align: 'right' });
  doc.text('RATE', colRateEnd - 2, tableTop + 5, { align: 'right' });
  doc.text('AMOUNT ($)', rightEdge - 2, tableTop + 5, { align: 'right' });

  y = tableTop + headerHeight;
  let subtotal = 0;

  lineItems.forEach((item, index) => {
    const qty = parseFloat(item.qty) || 0;
    const rate = parseFloat(item.amount) || 0;
    const lineGross = qty * rate;
    const lineDisc = lineGross * ((parseFloat(item.discount) || 0) / 100);
    const amount = lineGross - lineDisc;
    subtotal += amount;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    const nameText = doc.splitTextToSize(safe(item.description), colCode - colName - 4);
    const codeText = doc.splitTextToSize(safe(item.item || ''), colHrEnd - colCode - 16);
    const maxLines = Math.max(nameText.length, codeText.length, 1);
    const effectiveRowHeight = Math.max(rowHeight, maxLines * 4 + 4);

    if (y + effectiveRowHeight > 270) {
      doc.setDrawColor(...colors.text);
      doc.setLineWidth(0.3);
      doc.line(leftMargin, y, rightEdge, y);
      doc.addPage();
      doc.setFillColor(...colors.white);
      doc.rect(0, 0, 210, 297, 'F');
      y = 18;
      doc.setFillColor(...colors.primary);
      doc.rect(leftMargin, y, contentWidth, headerHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...colors.white);
      doc.text('NAME', colName + 2, y + 5);
      doc.text('COST CODE', colCode + 2, y + 5);
      doc.text('HOURS', colHrEnd - 2, y + 5, { align: 'right' });
      doc.text('RATE', colRateEnd - 2, y + 5, { align: 'right' });
      doc.text('AMOUNT ($)', rightEdge - 2, y + 5, { align: 'right' });
      y += headerHeight;
    }

    if (index % 2 === 1) {
      doc.setFillColor(...colors.rowAlt);
      doc.rect(leftMargin, y, contentWidth, effectiveRowHeight, 'F');
    }

    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.1);
    doc.line(leftMargin, y + effectiveRowHeight, rightEdge, y + effectiveRowHeight);

    doc.setTextColor(...colors.text);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    doc.text(nameText, colName + 2, y + 5);
    doc.text(codeText, colCode + 2, y + 5);
    doc.text(String(qty), colHrEnd - 2, y + 5, { align: 'right' });
    doc.text(fmtMoney(rate), colRateEnd - 2, y + 5, { align: 'right' });
    doc.text(fmtMoney(amount), rightEdge - 2, y + 5, { align: 'right' });

    y += effectiveRowHeight;
  });

  doc.setDrawColor(...colors.text);
  doc.setLineWidth(0.3);
  doc.line(leftMargin, y, rightEdge, y);

  y += 1;
  doc.setFillColor(...colors.accent);
  doc.rect(summaryBarX, y, rightEdge - summaryBarX, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...colors.text);
  doc.text('Sub-Total ($)', summaryBarX + 2, y + 5);
  doc.text(fmtMoney(subtotal), rightEdge - 2, y + 5, { align: 'right' });
  y += 7;

  if (y > 240) {
    doc.addPage();
    doc.setFillColor(...colors.white);
    doc.rect(0, 0, 210, 297, 'F');
    y = 25;
  } else {
    y += 12;
  }

  let afterDiscount = subtotal;

  if (discountRate > 0) {
    const discountAmount = subtotal * (discountRate / 100);
    afterDiscount = subtotal - discountAmount;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...colors.text);
    doc.text(`Discount @ ${discountRate}%`, leftMargin, y + 5);
    doc.text(`${discountRate}%`, summaryBarX + 22, y + 5);
    doc.text(fmtMoney(discountAmount), rightEdge - 2, y + 5, { align: 'right' });
    y += 8;
  }

  const taxAmount = afterDiscount * ((taxRate || 0) / 100);

  if (taxRate > 0) {
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.1);
    doc.line(leftMargin, y, rightEdge, y);
    y += 2;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...colors.text);
    doc.text(`GST @ ${taxRate}%`, leftMargin, y + 5);
    doc.text(`${taxRate}%`, summaryBarX + 22, y + 5);
    doc.text(fmtMoney(taxAmount), rightEdge - 2, y + 5, { align: 'right' });
    y += 10;
  }

  const finalTotal = afterDiscount + taxAmount;
  doc.setFillColor(...colors.primary);
  doc.rect(summaryBarX, y, rightEdge - summaryBarX, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...colors.white);
  doc.text('TOTAL ($)', summaryBarX + 2, y + 6);
  doc.text(`$ ${fmtMoney(finalTotal)}`, rightEdge - 2, y + 6, { align: 'right' });
  y += 16;

  // ── Notes ── (the wizard's Notes field; nothing is drawn when it is blank)
  const notesText = safe(notes).trim();
  if (notesText) {
    // The TOTAL bar leaves white bold 10pt set; the block sets its own colour, font and size.
    const newPageIf = (overflows) => {
      if (overflows) {
        doc.addPage();
        doc.setFillColor(...colors.white);
        doc.rect(0, 0, 210, 297, 'F');
        y = 25;
      }
    };
    doc.setTextColor(...colors.text);
    newPageIf(y + 8 > 270); // keep the label on the same page as the first line
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('NOTES', leftMargin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.splitTextToSize(notesText, contentWidth).forEach((line) => {
      newPageIf(y > 270);
      doc.text(line, leftMargin, y);
      y += 4;
    });
    y += 6;
  }

  // ── Footer ── (the notes block above replaced the "Thank you for your business!" line)
  if (url) {
    if (y > 270) {
      doc.addPage();
      doc.setFillColor(...colors.white);
      doc.rect(0, 0, 210, 297, 'F');
      y = 25;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...colors.lightText);
    doc.text(safe(url), leftMargin, y);
  }

  // ── Data Dump Pages ──
  if (dataDump) {
    const parseHours = (tr) => {
      const s = String(tr || '').trim();
      if (!s) return 0;
      if (s.includes(':')) {
        const parts = s.split(':');
        return (parseInt(parts[0] || '0', 10) || 0) + (parseInt(parts[1] || '0', 10) || 0) / 60 + (parseInt(parts[2] || '0', 10) || 0) / 3600;
      }
      return parseFloat(s) || 0;
    };

    const dumpHeaderHeight = 7;
    const dumpRowHeight = 6;
    const dumpFontSize = 7;

    const renderDumpSection = (title, columns, rows) => {
      if (!rows || rows.length === 0) return;

      doc.addPage();
      doc.setFillColor(...colors.white);
      doc.rect(0, 0, 210, 297, 'F');
      let dy = 15;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...colors.primary);
      doc.text(title, leftMargin, dy);
      if (billingPeriod) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...colors.text);
        doc.text(safe(billingPeriod), rightEdge, dy, { align: 'right' });
      }
      dy += 8;

      const metaLabelX = leftMargin;
      const metaValueX = leftMargin + 32;
      const metaRows = [
        { label: 'Project:', value: `${safe(matterId)} ${safe(matterName)}` },
        {
          label: 'Client:',
          value:
            safe(client?.name) +
            (client?.id ? ` — ID: ${safe(client.id)}` : '') +
            (client?.company ? ` (${safe(client.company)})` : ''),
        },
        { label: 'Staff:', value: 'All Staff' },
        { label: 'Time-track status:', value: 'Both approved and not approved time-track' },
      ];
      metaRows.forEach((m) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...colors.text);
        doc.text(m.label, metaLabelX, dy);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, metaValueX, dy);
        dy += 4;
      });

      dy += 4;

      doc.setFillColor(...colors.primary);
      doc.rect(leftMargin, dy, contentWidth, dumpHeaderHeight, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(dumpFontSize);
      doc.setTextColor(...colors.white);
      columns.forEach((col) => {
        if (col.align === 'right') {
          doc.text(col.header, col.x + col.w - 2, dy + 5, { align: 'right' });
        } else {
          doc.text(col.header, col.x + 2, dy + 5);
        }
      });
      dy += dumpHeaderHeight;

      let groupTotal = 0;

      rows.forEach((row, idx) => {
        if (row._isGroupHeader) {
          if (idx > 0) {
            doc.setDrawColor(...colors.border);
            doc.setLineWidth(0.2);
            doc.line(leftMargin, dy, rightEdge, dy);
          }
          dy += 2;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...colors.text);
          doc.text(row.label, leftMargin + 1, dy + 4);
          if (row.subtotal !== undefined) {
            doc.text(fmtMoney(row.subtotal), rightEdge - 2, dy + 4, { align: 'right' });
          }
          dy += dumpRowHeight + 1;
          return;
        }

        if (dy > 270) {
          doc.addPage();
          doc.setFillColor(...colors.white);
          doc.rect(0, 0, 210, 297, 'F');
          dy = 15;
          doc.setFillColor(...colors.primary);
          doc.rect(leftMargin, dy, contentWidth, dumpHeaderHeight, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(dumpFontSize);
          doc.setTextColor(...colors.white);
          columns.forEach((col) => {
            if (col.align === 'right') {
              doc.text(col.header, col.x + col.w - 2, dy + 5, { align: 'right' });
            } else {
              doc.text(col.header, col.x + 2, dy + 5);
            }
          });
          dy += dumpHeaderHeight;
        }

        if (idx % 2 === 0) {
          doc.setFillColor(...colors.rowAlt);
          doc.rect(leftMargin, dy, contentWidth, dumpRowHeight, 'F');
        }

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(dumpFontSize);
        doc.setTextColor(...colors.text);

        columns.forEach((col) => {
          const val = safe(row[col.key]);
          const maxW = col.w - 4;
          const clipped = doc.splitTextToSize(val, maxW)[0] || '';
          if (col.align === 'right') {
            doc.text(clipped, col.x + col.w - 2, dy + 4, { align: 'right' });
          } else {
            doc.text(clipped, col.x + 2, dy + 4);
          }
        });

        groupTotal += parseFloat(row._amount || 0);
        dy += dumpRowHeight;
      });

      dy += 2;
      doc.setFillColor(...colors.accent);
      doc.rect(leftMargin, dy, contentWidth, dumpRowHeight + 1, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...colors.text);
      doc.text('Total', leftMargin + 2, dy + 5);
      doc.text(fmtMoney(groupTotal), rightEdge - 2, dy + 5, { align: 'right' });
    };

    // ── Billable Time ──
    if (dataDump.billableTimes && dataDump.billableTimes.length > 0) {
      const btCols = [
        { header: 'Employee', key: 'employee', x: leftMargin, w: 32 },
        { header: 'Project #', key: 'project', x: leftMargin + 32, w: 28 },
        { header: 'Notes', key: 'notes', x: leftMargin + 60, w: 42 },
        { header: 'Date', key: 'date', x: leftMargin + 102, w: 28 },
        { header: 'Hours', key: 'hours', x: leftMargin + 130, w: 16, align: 'right' },
        { header: 'Rate', key: 'rate', x: leftMargin + 146, w: 16, align: 'right' },
        { header: 'Amount', key: 'amount', x: leftMargin + 162, w: 18, align: 'right' },
      ];

      const grouped = {};
      dataDump.billableTimes.forEach((bt) => {
        const emp = bt.employee_name || 'Unknown';
        if (!grouped[emp]) grouped[emp] = [];
        grouped[emp].push(bt);
      });

      const btRows = [];
      Object.keys(grouped).sort().forEach((emp) => {
        const items = grouped[emp];
        const empTotal = items.reduce(
          (s, bt) => s + (bt.stored_amount != null ? (parseFloat(bt.stored_amount) || 0) : computeBilledTimeAmount(bt)),
          0
        );
        btRows.push({ _isGroupHeader: true, label: emp, subtotal: empTotal });
        items.forEach((bt) => {
          const h = parseHours(bt.time_recorded);
          const amt = bt.stored_amount != null ? (parseFloat(bt.stored_amount) || 0) : computeBilledTimeAmount(bt);
          // Effective rate so hours × rate matches the billed amount
          const r = h > 0 ? amt / h : parseFloat(bt.billedrate) || 0;
          btRows.push({
            employee: emp,
            project: bt.matterid || safe(matterId),
            notes: bt.notes || '',
            date: formatDate(bt.date_recorded || bt.date || ''),
            hours: h.toFixed(2),
            rate: fmtMoney(r),
            amount: fmtMoney(amt),
            _amount: amt,
          });
        });
      });

      renderDumpSection('Billable Time Detail', btCols, btRows);
    }

    // ── Fixed Fees ──
    if (dataDump.fixedFees && dataDump.fixedFees.length > 0) {
      const ffCols = [
        { header: 'Fee Code', key: 'code', x: leftMargin, w: 30 },
        { header: 'Narrative', key: 'narrative', x: leftMargin + 30, w: 70 },
        { header: 'Project #', key: 'project', x: leftMargin + 100, w: 30 },
        { header: 'Date', key: 'date', x: leftMargin + 130, w: 28 },
        { header: 'Amount', key: 'amount', x: leftMargin + 158, w: 22, align: 'right' },
      ];

      const ffRows = dataDump.fixedFees.map((ff) => {
        const amt = parseFloat(ff.fees) || 0;
        return {
          code: ff.fee_code || '',
          narrative: ff.narrative || '',
          project: ff.matterid || ff.matter_id || safe(matterId),
          date: formatDate(ff.date_added || ''),
          amount: fmtMoney(amt),
          _amount: amt,
        };
      });

      renderDumpSection('Fixed Fees Detail', ffCols, ffRows);
    }

    // ── Expenses ──
    if (dataDump.expenses && dataDump.expenses.length > 0) {
      const expCols = [
        { header: 'Description', key: 'desc', x: leftMargin, w: 50 },
        { header: 'Category', key: 'category', x: leftMargin + 50, w: 22 },
        { header: 'Project #', key: 'project', x: leftMargin + 72, w: 28 },
        { header: 'Date', key: 'date', x: leftMargin + 100, w: 28 },
        { header: 'Qty', key: 'qty', x: leftMargin + 128, w: 14, align: 'right' },
        { header: 'Amount', key: 'amount', x: leftMargin + 142, w: 18, align: 'right' },
      ];

      const getExpCategory = (item) => {
        if (item.ge_id != null || item.gen_id != null) return 'General';
        if (item.opp_id != null) return 'Operational';
        if (item.ven_id != null) return 'Vendor';
        return 'Disbursement';
      };

      const expRows = dataDump.expenses.map((exp) => {
        const amt = typeof exp.amount === 'number' ? exp.amount : parseFloat(exp.amount || exp.fees || '0') || 0;
        return {
          desc: exp.description || 'Disbursement',
          category: getExpCategory(exp),
          project: exp.matterid || exp.matter_id || safe(matterId),
          date: formatDate(exp.expense_date || exp.date || exp.date_added || ''),
          qty: String(typeof exp.quantity === 'number' ? exp.quantity : 1),
          amount: fmtMoney(amt),
          _amount: amt,
        };
      });

      renderDumpSection('Expenses Detail', expCols, expRows);
    }
  }

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...colors.lightText);
    const footerY = doc.internal.pageSize.getHeight() - 7;
    if (gstNumber) {
      doc.text(`GST # ${safe(gstNumber)}`, leftMargin, footerY);
    }
    doc.text(`Page ${p} of ${totalPages}`, rightEdge, footerY, { align: 'right' });
  }

  if (status === 'void') {
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.saveGraphicsState();
      const gState = new doc.GState({ opacity: 0.15 });
      doc.setGState(gState);
      doc.setFontSize(80);
      doc.setTextColor(185, 28, 28);
      doc.text('VOID', 105, 160, { align: 'center', angle: 45 });
      doc.restoreGraphicsState();
    }
  }

  // PDF "Invoice Number" uses the value from the form (custom or auto); filename must be filesystem-safe.
  const fileSlug = String(safe(invoiceId) || 'draft')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_')
    .slice(0, 120);
  const filename = `invoice_${fileSlug || 'draft'}.pdf`;
  doc.save(filename);

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  return { pdfBlob, pdfUrl };
};

export default generateInvoicePDF;
